import React, { useState, useRef, useEffect, useLayoutEffect, forwardRef, useImperativeHandle, useMemo, useContext, useCallback } from "react";
import classnames from "classnames";
import { buildAriaProps, buildCss, buildDataProps, buildHtmlProps } from "../utilities/props";
import { globalProps } from "../utilities/globalProps";
import { GenericObject } from "../types";

import Body from '../pb_body/_body';
import Caption from "../pb_caption/_caption";
import colors from "../tokens/exports/_colors.module.scss";

import DropdownContainer from "./subcomponents/DropdownContainer";
import DropdownContext from "./context";
import DropdownOption from "./subcomponents/DropdownOption";
import DropdownTrigger from "./subcomponents/DropdownTrigger";
import DropdownFormFields from "./subcomponents/DropdownFormFields";
import useDropdown from "./hooks/useDropdown";
import useAsyncOptions, { LoadOptions } from "./hooks/useAsyncOptions";
import { getCreationOption, matchesOptionExactly } from "./utilities/optionCreation";
import getQuickPickOptions from "./quickpick";

import {
    separateChildComponents,
    prepareSubcomponents,
    handleClickOutside,
    setQuickPickDateOnPicker,
} from "./utilities";

import { DialogContext } from "../pb_dialog/_dialog_context";
import {
    resolveFloatingOwnerId,
    resolvePortaledKitHost,
    positionDropdownPortalToWrapper,
    subscribeFloatingKitReposition,
} from "../utilities/floatingPortalHosts";
        
function serializeDropdownFilterResetDefault(
    variant: "default" | "subtle" | "quickpick" | undefined,
    multiSelect: boolean,
    defaultValue: GenericObject | GenericObject[] | string | undefined,
    dropdownOptions: GenericObject[] | GenericObject | undefined,
): string | undefined {
    const optionList: GenericObject[] = Array.isArray(dropdownOptions)
        ? dropdownOptions
        : [];
    const optionDefaultId = (option: GenericObject | undefined): string | undefined => {
        if (!option) return undefined;

        const id = option.id;
        if (id != null && id !== "") return String(id);

        const matched = optionList.find((listOption: GenericObject) => (
            (option.value != null && listOption.value === option.value) ||
            (option.label != null && listOption.label === option.label)
        ));

        if (matched?.id != null && matched.id !== "") return String(matched.id);
        return undefined;
    };

    if (variant === "quickpick") {
        if (typeof defaultValue === "string" && defaultValue) {
            const matched = optionList.find(
                (opt: GenericObject) => opt.label?.toLowerCase() === defaultValue.toLowerCase()
            );
            if (matched?.id != null && matched.id !== "") return String(matched.id);
        }
        return undefined;
    }
    if (multiSelect) {
        const arr = Array.isArray(defaultValue)
            ? defaultValue
            : defaultValue && typeof defaultValue === "object" && Object.keys(defaultValue).length
                ? [defaultValue as GenericObject]
                : [];
        if (!arr.length) return undefined;
        const ids = arr
            .map((v) => optionDefaultId(v as GenericObject))
            .filter((id) => id != null && id !== "");
        return ids.length ? ids.join(",") : undefined;
    }
    if (defaultValue && typeof defaultValue === "object" && !Array.isArray(defaultValue)) {
        const id = optionDefaultId(defaultValue as GenericObject);
        if (id) return id;
    }
    return undefined;
}

// Async results are server records, so identify them by value like Typeahead rather than by label.
const asyncOptionValue = (option: GenericObject) => option.value ?? option.id ?? option.label;

type CustomQuickPickDate = {
    label: string;
    value: Array<Date | string> | { timePeriod: string; amount: number };
};

type CustomQuickPickDates = {
    override?: boolean;
    dates: CustomQuickPickDate[];
};

type InputChangeReason = "input" | "clear";

type DropdownProps = {
    allowOptionCreation?: boolean;
    aria?: { [key: string]: string };
    async?: boolean;
    loadOptions?: LoadOptions;
    defaultOptions?: boolean | GenericObject[];
    cacheOptions?: boolean;
    loading?: boolean;
    onInputChange?: (input: string, detail: { reason: InputChangeReason }) => void;
    value?: GenericObject | GenericObject[] | null;
    renderOption?: (option: GenericObject) => React.ReactNode;
    autocomplete?: boolean;
    blankSelection?: string;
    children?: React.ReactChild[] | React.ReactChild | React.ReactElement[];
    className?: string;
    clearable?: boolean;
    closeOnClick?: "outside" | "inside" | "any";
    constrainHeight?: boolean;
    customQuickPickDates?: CustomQuickPickDates;
    disabled?: boolean;
    formPillProps?: GenericObject;
    dark?: boolean;
    data?: { [key: string]: string };
    defaultValue?: GenericObject;
    error?: string;
    enableFormSupport?: boolean;
    enablePillReorder?: boolean;
    externalDragHandle?: boolean;
    htmlOptions?: { [key: string]: string | number | boolean | (() => void) },
    id?: string;
    isClosed?: boolean;
    label?: string;
    multiSelect?: boolean;
    name?: string;
    noOptionsText?: string;
    onChange?: (event: { target: { name?: string; value: any } }) => void;
    onSelect?: (arg: GenericObject) => null;
    options?: GenericObject;
    placeholder?: string;
    separators?: boolean;
    showPillIndex?: boolean;
    variant?: "default" | "subtle" | "quickpick";
    rangeEndsToday?: boolean;
    controlsStartId?: string;
    controlsEndId?: string;
    activeStyle?: {
      backgroundColor?: string;
      fontColor?: string;
    };
    requiredIndicator?: boolean;
    required?: boolean;
    validationMessage?: string;
};

interface DropdownComponent
    extends React.ForwardRefExoticComponent<DropdownProps & React.RefAttributes<unknown>> {
    Option: typeof DropdownOption;
    Trigger: typeof DropdownTrigger;
    Container: typeof DropdownContainer;
}

let Dropdown = (props: DropdownProps, ref: any): React.ReactElement | null => {
    const {
        allowOptionCreation = false,
        aria = {},
        async = false,
        loadOptions,
        defaultOptions = false,
        cacheOptions = false,
        loading = false,
        onInputChange,
        renderOption,
        value,
        autocomplete: autocompleteProp = false,
        blankSelection = '',
        children,
        className,
        clearable = true,
        closeOnClick = "any",
        constrainHeight = false,
        customQuickPickDates,
        dark = false,
        data = {},
        defaultValue = {},
        disabled = false,
        error: errorProp,
        enableFormSupport = false,
        enablePillReorder = false,
        externalDragHandle = false,
        htmlOptions = {},
        id,
        isClosed = true,
        label,
        multiSelect = false,
        formPillProps,
        name,
        noOptionsText,
        onChange,
        onSelect,
        options,
        placeholder,
        showPillIndex = false,
        rangeEndsToday = false,
        controlsStartId,
        controlsEndId,
        separators = true,
        variant = "default",
        activeStyle,
        requiredIndicator = false,
        required = false,
        validationMessage = "Please fill out this field."
    } = props;

    const isPillReorderEnabled = Boolean(enablePillReorder && multiSelect);

    const creationEnabled = allowOptionCreation && variant !== "quickpick";
    const autocomplete = autocompleteProp || creationEnabled;

    const [formInvalid, setFormInvalid] = useState(false);
    const formError = formInvalid ? validationMessage : "";
    const error = enableFormSupport ? errorProp || formError : errorProp;

    const ariaProps = buildAriaProps(aria);
    const dataProps = buildDataProps(data);
    const htmlProps = buildHtmlProps(htmlOptions);
    const separatorsClass = separators ? '' : 'separators_hidden'
    const classes = classnames(
        buildCss("pb_dropdown", variant, separatorsClass),
        disabled && "disabled",
        globalProps(props),
        className
    );

    const floatingShellClasses = useMemo(
        () => classnames(classes, "pb_dropdown_floating_shell"),
        [classes],
    );

    const asyncEnabled = async && variant !== "quickpick";
    const getOptionValue = asyncEnabled ? asyncOptionValue : undefined;
    const { options: loadedOptions, status: asyncStatus, search: searchAsync, cancel: cancelAsync, resume: resumeAsync, clear: clearAsync } = useAsyncOptions(asyncEnabled && !!loadOptions && !disabled, loadOptions, defaultOptions, cacheOptions);

    // ------------- Quick Pick ---------------------------------
    // Use QuickPick options when variant is "quickpick"
    const dropdownOptions = variant === "quickpick" 
        ? getQuickPickOptions(rangeEndsToday, customQuickPickDates) 
        : (asyncEnabled && loadOptions ? loadedOptions : options || []);
    // ----------------------------------------------------------

    const [isDropDownClosed, setIsDropDownClosed, toggleDropdown] = useDropdown(disabled ? true : isClosed);

    const previouslyClosed = useRef(isDropDownClosed);
    const queryRef = useRef("");
    useLayoutEffect(() => {
      if (asyncEnabled && (disabled || (!previouslyClosed.current && isDropDownClosed))) cancelAsync();
      if (asyncEnabled && !disabled && previouslyClosed.current && !isDropDownClosed) resumeAsync(queryRef.current);
      previouslyClosed.current = isDropDownClosed;
    }, [asyncEnabled, isDropDownClosed, disabled, cancelAsync, resumeAsync]);

    // Use a suffix for the trigger ID to avoid conflict with the outer div's id
    const sanitizeForId = (str: string) =>
      str.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const selectId = id
      ? `${id}_trigger`
      : label
        ? sanitizeForId(label)
        : undefined;
    const errorId = error ? `${selectId}-error` : undefined;

    const initialSelected = useMemo(() => {
      // Handle quickpick variant with string defaultValue (e.g., "This Month")
      if (variant === "quickpick" && typeof defaultValue === "string" && defaultValue) {
        const matchedOption = dropdownOptions.find(
          (opt: GenericObject) => opt.label?.toLowerCase() === (defaultValue as string).toLowerCase()
        );
        return matchedOption || {};
      }
      
      if (multiSelect) {
        if (Array.isArray(defaultValue)) return defaultValue;
        return defaultValue && Object.keys(defaultValue).length
          ? [defaultValue]
          : [];
      }
      return defaultValue || {};
    }, [multiSelect, defaultValue, variant, dropdownOptions]);

    const [internalSelected, setInternalSelected] = useState<GenericObject | GenericObject[]>(
      initialSelected
    );

    const optionKey = (option: GenericObject) => String(
      getOptionValue ? getOptionValue(option) : option.id ?? option.value ?? option.label
    );

    const isControlled = value !== undefined;
    const selected = isControlled ? (value ?? (multiSelect ? [] : {})) : internalSelected;
    const setSelected: React.Dispatch<React.SetStateAction<GenericObject | GenericObject[]>> = (next) => {
      if (!isControlled) setInternalSelected(next);
    };
    const isSameOption = useCallback((left: GenericObject, right: GenericObject, legacyKey = "label") => {
      if (!getOptionValue) return left?.[legacyKey] === right?.[legacyKey];
      if (!left || !right || !Object.keys(left).length || !Object.keys(right).length) return false;
      return String(getOptionValue(left)) === String(getOptionValue(right));
    }, [getOptionValue]);

    // Autocomplete displays the selection in the input; seed from defaultValue
    const [filterItem, setFilterItem] = useState(() => {
      if (!autocomplete || multiSelect) return "";
      if (Array.isArray(selected)) return "";
      return (selected as GenericObject)?.label || "";
    });
    queryRef.current = filterItem;

    const preserveQueryOnControlledClear = useRef(false);

    // Form adapters may recreate equivalent option objects on every render.
    // Synchronize the query only when selection identity or display text changes.
    const controlledSelectionKey = isControlled ? JSON.stringify(
      (Array.isArray(value) ? value : value && Object.keys(value).length ? [value] : [])
        .map((option) => [
          String(getOptionValue ? getOptionValue(option) : option.id ?? option.value ?? option.label),
          option.label,
        ])
    ) : undefined;
    const controlledLabel = !multiSelect && value && !Array.isArray(value) ? value?.label || "" : "";

    useEffect(() => {
      if (isControlled && autocomplete) {
        // Accepting a selection clear must not erase the character that started a new search.
        if (controlledLabel || !preserveQueryOnControlledClear.current) setFilterItem(controlledLabel);
        preserveQueryOnControlledClear.current = false;
      }
    }, [isControlled, controlledSelectionKey, controlledLabel, autocomplete, multiSelect]);

    const filterResetDefaultSerialized = useMemo(
        () => serializeDropdownFilterResetDefault(variant, multiSelect, defaultValue, dropdownOptions),
        [variant, multiSelect, defaultValue, dropdownOptions]
    );

    const [isInputFocused, setIsInputFocused] = useState(false);
    const [hasTriggerSubcomponent, setHasTriggerSubcomponent] = useState(true);
    const [hasContainerSubcomponent, setHasContainerSubcomponent] =
        useState(true);
    //state for keyboard events
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);

    const dropdownRef = useRef(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const inputWrapperRef = useRef<HTMLDivElement | null>(null);
    const dropdownContainerRef = useRef(null);
    const outerDivRef = useRef<HTMLDivElement>(null);

    const dialogCtx = useContext(DialogContext);
    const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
    const [floatingOwnerId, setFloatingOwnerId] = useState<string | null>(null);

    useLayoutEffect(() => {
        if (isDropDownClosed) {
            setPortalHost(null);
            setFloatingOwnerId(null);
            return;
        }
        const root = outerDivRef.current;
        setFloatingOwnerId(resolveFloatingOwnerId(root));
        setPortalHost(
            resolvePortaledKitHost(
                root,
                dialogCtx?.selectMenuPortalTarget ?? null,
            ),
        );
    }, [isDropDownClosed, dialogCtx?.selectMenuPortalTarget]);

    const handleLabelClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      if (selectId) {
        const trigger = document.getElementById(selectId);
        if (trigger) trigger.focus();
      }
      setIsInputFocused(true);
      toggleDropdown();
    };

    const selectedArray = Array.isArray(selected)
    ? selected
    : selected && Object.keys(selected).length
    ? [selected]
    : [];

    const hasFormSelection = enableFormSupport && selectedArray.some((option) => String(option.id ?? option.value ?? "") !== "");
    useEffect(() => {
        if (!enableFormSupport || hasFormSelection || !required) setFormInvalid(false);
    }, [enableFormSupport, hasFormSelection, required]);

    const selectedOptionIds = useMemo(() => (
      multiSelect && getOptionValue
        ? new Set(selectedArray.map((option) => String(getOptionValue(option))))
        : null
    ), [multiSelect, getOptionValue, selectedArray]);

    const { trigger, container, otherChildren } =
        separateChildComponents(children);

    useEffect(() => {
     // Handle clicks outside the dropdown
        const handleClick = handleClickOutside({
            inputWrapperRef,
            dropdownContainerRef,
            setIsDropDownClosed,
            setFocusedOptionIndex,
            setIsInputFocused,
            closeOnClick,
        });

        window.addEventListener("click", handleClick);
        return () => {
            window.removeEventListener("click", handleClick);
        };
    }, [closeOnClick]);

    useEffect(() => {
        setHasTriggerSubcomponent(!!trigger);
        setHasContainerSubcomponent(!!container);
    }, []);

    // dropdown to toggle with external control
    useEffect(() => {
        setIsDropDownClosed(disabled ? true : isClosed)
    }, [disabled, isClosed])

    // blankSelection is a clear action, not a search result, so it cannot stand in for an empty async list.
    const blankSelectionOption: GenericObject = blankSelection && (!asyncEnabled || dropdownOptions.length > 0)
        ? [{ label: blankSelection, value: "" }]
        : [];
    const optionsWithBlankSelection = blankSelectionOption.concat(dropdownOptions);

    const availableOptions = useMemo(()=> {
        if (!multiSelect) return optionsWithBlankSelection;
        if (selectedOptionIds) {
          return optionsWithBlankSelection.filter((option: GenericObject) => !selectedOptionIds.has(String(getOptionValue(option))));
        }
        return optionsWithBlankSelection.filter((option: GenericObject) => !selectedArray.some((sel) => isSameOption(sel, option)));
    }, [optionsWithBlankSelection, selectedArray, multiSelect, isSameOption, selectedOptionIds, getOptionValue]);
    
    const searchIsPending = loading || (asyncEnabled && asyncStatus === "loading");
    const searchFailed = asyncEnabled && asyncStatus === "error";
    const canCreateOption = creationEnabled && !disabled && !searchIsPending && !searchFailed;
    let creationOption: GenericObject | null = null;
    if (canCreateOption) {
        const existingOptions = Array.isArray(dropdownOptions) ? dropdownOptions : [];
        creationOption = getCreationOption(filterItem, [...existingOptions, ...selectedArray]);
    }

    const matchingOptions = useMemo(() => {
          const selectedLabel =
            !multiSelect &&
            !Array.isArray(selected) &&
            (selected as GenericObject)?.label;
          // Async searches stay unfiltered until the input is showing the selected label.
          // Reopening then shows only results that match that text.
          if (asyncEnabled) {
            if (!(autocomplete && selectedLabel && filterItem === selectedLabel)) return availableOptions;
            const query = String(selectedLabel).toLowerCase();
            return availableOptions.filter((opt: GenericObject) => String(opt.label).toLowerCase().includes(query));
          }
          // When the input shows the selected label, do not filter the list down to that one option
          const filterText =
            selectedLabel && filterItem === selectedLabel ? "" : filterItem;
          return availableOptions.filter((opt: GenericObject) =>
            String(opt.label).toLowerCase().includes(filterText.toLowerCase()) ||
            (creationEnabled && matchesOptionExactly(opt, filterText))
          );
        }, [availableOptions, filterItem, multiSelect, selected, asyncEnabled, autocomplete, creationEnabled]);

    const filteredOptions = creationOption ? [...matchingOptions, creationOption] : matchingOptions;
    const inputShowsSelection = !multiSelect && !Array.isArray(selected) && filterItem === selected?.label;
    let creationFocusIndex = -1;
    if (creationEnabled && creationOption) {
        creationFocusIndex = matchingOptions.length;
    } else if (creationEnabled) {
        creationFocusIndex = matchingOptions.findIndex((option: GenericObject) => !option.disabled);
    }

    useEffect(() => {
        if (!creationEnabled) return;
        if (isDropDownClosed || !filterItem.trim() || inputShowsSelection) {
            setFocusedOptionIndex(-1);
            return;
        }

        setFocusedOptionIndex(creationFocusIndex);
    }, [creationEnabled, isDropDownClosed, filterItem, inputShowsSelection, creationFocusIndex]);

    // Empty copy follows the settled search. Hiding already-selected rows must not turn a
    // successful result list into "No results found", including the last pick while the menu stays open.
    const asyncEmptyCopy = asyncEnabled && (asyncStatus === "empty" || (asyncStatus === "idle" && dropdownOptions.length === 0))
        ? noOptionsText || "No results found"
        : "";

    let asyncStatusText = "";
    if (asyncEnabled) {
        if (searchIsPending) asyncStatusText = "Loading…";
        else if (searchFailed) asyncStatusText = "Unable to load options";
        else if (!creationOption) asyncStatusText = asyncEmptyCopy;
    }

    // A keystroke clears the highlight. Focus the first result once the next list arrives,
    // including options the application loads itself.
    const asyncResultOptions = loadOptions ? loadedOptions : options;
    useEffect(() => {
        if (!creationEnabled && asyncEnabled && !isDropDownClosed) {
            setFocusedOptionIndex(filteredOptions.length ? 0 : -1);
        }
    }, [asyncResultOptions, creationEnabled]);

    // For keyboard accessibility: Set focus within dropdown to selected item if it exists
    useEffect(() => {
        if (!creationEnabled && !isDropDownClosed) {
            let newIndex = 0;
            if (selected && !Array.isArray(selected) && selected.label) {
                const selectedIndex = filteredOptions.findIndex((option: GenericObject) => isSameOption(option, selected));
                if (selectedIndex >= 0) {
                    newIndex = selectedIndex;
                }
            }
            setFocusedOptionIndex(newIndex);
        }
    }, [isDropDownClosed, creationEnabled]);

    // Auto-position dropdown above/below based on available space or fixed/absolute when portaled to body / floating root
    useLayoutEffect(() => {
        if (isDropDownClosed || !dropdownContainerRef.current || !dropdownRef.current) {
            return;
        }

        const container = dropdownContainerRef.current as HTMLElement;
        const wrapper = dropdownRef.current as HTMLElement;

        if (portalHost) {
            const applyPortalPosition = () => {
                const panel = dropdownContainerRef.current as HTMLElement | null;
                const wrap = dropdownRef.current as HTMLElement | null;
                if (!panel || !wrap) {
                    return;
                }
                positionDropdownPortalToWrapper({
                    panel,
                    wrapperViewportRect: wrap.getBoundingClientRect(),
                    positionHost: portalHost,
                });
            };
            applyPortalPosition();
            const raf = window.requestAnimationFrame(applyPortalPosition);
            const unsubscribeReposition = subscribeFloatingKitReposition(applyPortalPosition);
            return () => {
                window.cancelAnimationFrame(raf);
                unsubscribeReposition();
            };
        }

        const wrapperRect = wrapper.getBoundingClientRect();
        const h = container.getBoundingClientRect().height || container.scrollHeight;
        const spaceBelow = window.innerHeight - wrapperRect.bottom;
        const spaceAbove = wrapperRect.top;

        if (spaceBelow < h + 10 && spaceAbove >= h + 10) {
            container.style.top = "auto";
            container.style.bottom = "calc(100% + 5px)";
            container.style.marginTop = "0";
            container.style.marginBottom = "0";
        } else {
            container.style.top = "";
            container.style.bottom = "";
            container.style.marginTop = "";
            container.style.marginBottom = "";
        }
    }, [
        isDropDownClosed,
        portalHost,
        filteredOptions,
        constrainHeight,
        filterItem,
    ]);


    const handleSelectionChange = (value: any) => {
        onSelect && onSelect(value);
        onChange && onChange({ target: { name, value } });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, replaceSelection = false) => {
        if (disabled) return;
        if (asyncEnabled) {
          searchAsync(e.target.value);
          setFocusedOptionIndex(-1);
        }
        if ((replaceSelection || ((asyncEnabled || onInputChange) && !e.target.value)) && autocomplete && !multiSelect && selectedArray.length) {
          preserveQueryOnControlledClear.current = isControlled && !!e.target.value;
          setSelected({});
          handleSelectionChange(null);
        }
        setFilterItem(e.target.value);
        onInputChange?.(e.target.value, { reason: "input" });
        setIsDropDownClosed(false);
    };


      const handleOptionClick = (clickedItem: GenericObject) => {
                if (disabled) return;
                preserveQueryOnControlledClear.current = false;
                // Single autocomplete keeps the search results so reopening can match the selected label.
                // Other selections still drop back to the initial list.
                if (asyncEnabled) {
                  if (!multiSelect && autocomplete) cancelAsync();
                  else clearAsync();
                }
                const shouldCloseOnClick = closeOnClick === "any" || closeOnClick === "inside";
                
                if (multiSelect) {
                  if (isControlled || getOptionValue) {
                    const exists = selectedOptionIds
                      ? selectedOptionIds.has(String(getOptionValue(clickedItem)))
                      : selectedArray.some((option) => isSameOption(option, clickedItem, "value"));
                    const next = exists
                      ? selectedArray.filter((option) => !isSameOption(option, clickedItem, "value"))
                      : [...selectedArray, clickedItem];
                    setSelected(next);
                    handleSelectionChange(next);
                  } else {
                    setSelected((prev) => {
                       const list = prev as GenericObject[];
                       const exists = list.find((option) => isSameOption(option, clickedItem, "value"));
                       const next = exists
                       ? list.filter((option) => !isSameOption(option, clickedItem, "value"))
                           : [...list, clickedItem];
                   handleSelectionChange(next);
                       return next;
                   });
                  }
                   setFilterItem("");
                   if (shouldCloseOnClick) {
                       setIsDropDownClosed(true);
                   }
               } else {
                   setSelected(clickedItem);
                   // Plain uncontrolled autocomplete clears its query after selection.
                   const keepSelectedLabel = autocomplete && (creationEnabled || asyncEnabled || isControlled);
                   const nextQuery = keepSelectedLabel
                     ? (isControlled ? (selected as GenericObject)?.label : clickedItem.label) || ""
                     : "";
                   setFilterItem(nextQuery);
                   if (shouldCloseOnClick) {
                       setIsDropDownClosed(true);
                   }
                   handleSelectionChange(clickedItem);
                   
                   // Sync with DatePickers if this is a quickpick variant
                   if (variant === "quickpick" && Array.isArray(clickedItem.value)) {
                       const [start, end] = clickedItem.value;
                       
                       if (controlsStartId) {
                           const startPicker = (document.querySelector(`#${controlsStartId}`) as HTMLElement & { _flatpickr?: any })?._flatpickr;
                           setQuickPickDateOnPicker(startPicker, start, "start");
                       }
                       
                       if (controlsEndId) {
                           const endPicker = (document.querySelector(`#${controlsEndId}`) as HTMLElement & { _flatpickr?: any })?._flatpickr;
                           setQuickPickDateOnPicker(endPicker, end, "end");
                       }
                   }
            }
             };

    const handleWrapperClick = () => {
        if (disabled) return;
        autocomplete && inputRef?.current?.focus();
        toggleDropdown();
    };

    const handleBackspace = () => {
      if (disabled) return;
      preserveQueryOnControlledClear.current = false;
      if (asyncEnabled) clearAsync();
      onInputChange?.("", { reason: "clear" });
      if ((onInputChange || asyncEnabled) && multiSelect) setFilterItem("");
      if (multiSelect) {
        setSelected([]);
        handleSelectionChange([]);
      } else {
        setSelected({});
        handleSelectionChange(null);
        setFocusedOptionIndex(-1);
        setFilterItem(isControlled && autocomplete && !multiSelect ? (selected as GenericObject)?.label || "" : "");
        
        // Clear linked DatePickers as well if this is a quickpick variant with controls
        if (variant === "quickpick") {
          if (controlsStartId) {
            const startPicker = (document.querySelector(`#${controlsStartId}`) as HTMLElement & { _flatpickr?: any })?._flatpickr;
            startPicker?.clear();
          }
          
          if (controlsEndId) {
            const endPicker = (document.querySelector(`#${controlsEndId}`) as HTMLElement & { _flatpickr?: any })?._flatpickr;
            endPicker?.clear();
          }
        }
      }
    };

    const componentsToRender = prepareSubcomponents({
        children,
        hasTriggerSubcomponent,
        hasContainerSubcomponent,
        trigger,
        container,
        otherChildren,
        dark
    });

    // Create an internal ref object that holds the imperative handle methods
    const imperativeRef = useRef({
      clearSelected: () => {
          preserveQueryOnControlledClear.current = false;
          if (asyncEnabled) clearAsync();
          onInputChange?.("", { reason: "clear" });
        if (multiSelect) {
          setSelected([]);
          handleSelectionChange([]);
        } else {
          setSelected({});
          handleSelectionChange(null);
        }
        setFilterItem(isControlled && autocomplete && !multiSelect ? (selected as GenericObject)?.label || "" : "");
        setIsDropDownClosed(true);
      },
    });

    // Update imperativeRef whenever dependencies change
    // (needed for external clearing of normal Dropdown + DatePicker-synced QuickPick Dropdown)
    useEffect(() => {
      imperativeRef.current = {
        clearSelected: () => {
          preserveQueryOnControlledClear.current = false;
          if (asyncEnabled) clearAsync();
          onInputChange?.("", { reason: "clear" });
          if (multiSelect) {
            setSelected([]);
            handleSelectionChange([]);
          } else {
            setSelected({});
            handleSelectionChange(null);
          }
          setFilterItem(isControlled && autocomplete && !multiSelect ? (selected as GenericObject)?.label || "" : "");
          setIsDropDownClosed(true);
        },
      };
    }, [multiSelect, handleSelectionChange, setSelected, setFilterItem, setIsDropDownClosed, asyncEnabled, clearAsync, onInputChange]);

    useImperativeHandle(ref, () => imperativeRef.current);

    useEffect(() => {
      // Attach the ref to the DOM element so DatePicker can access it
      if (outerDivRef.current && variant === "quickpick" && id) {
        (outerDivRef.current as any)._dropdownRef = imperativeRef;
      }
    }, [variant, id]);

    // Sync defaultValue with DatePickers on mount when 3 input pattern is used
    useEffect(() => {
      if (variant === "quickpick" && initialSelected && typeof initialSelected === "object" && !Array.isArray(initialSelected)) {
        const value = initialSelected.value;
        
        if (Array.isArray(value) && value.length === 2) {
          const [start, end] = value;
          
          // Wait for DatePickers to be initialized
          setTimeout(() => {
            if (controlsStartId) {
              const startPicker = (document.querySelector(`#${controlsStartId}`) as HTMLElement & { _flatpickr?: any })?._flatpickr;
              setQuickPickDateOnPicker(startPicker, start, "start");
            }
            
            if (controlsEndId) {
              const endPicker = (document.querySelector(`#${controlsEndId}`) as HTMLElement & { _flatpickr?: any })?._flatpickr;
              setQuickPickDateOnPicker(endPicker, end, "end");
            }
          }, 0);
        }
      }
    }, [variant, initialSelected, controlsStartId, controlsEndId]);

    const handleFormInvalid = () => {
        setFormInvalid(true);
        const focusTarget = inputRef.current || inputWrapperRef.current;
        focusTarget?.focus();
    };

    const handleFormReset = () => {
        const hasDefaultSelection = Object.keys(initialSelected).length > 0;
        const resetValue = multiSelect || hasDefaultSelection ? initialSelected : null;
        const resetQuery = autocomplete && !multiSelect
            ? (initialSelected as GenericObject).label || ""
            : "";

        setSelected(initialSelected);
        handleSelectionChange(resetValue);
        setFilterItem(resetQuery);
        setFormInvalid(false);
        setIsDropDownClosed(true);
        if (asyncEnabled) clearAsync();
    };

    return (
        <div {...ariaProps}
            {...dataProps}
            {...htmlProps}
            {...(filterResetDefaultSerialized ? { "data-default-value": filterResetDefaultSerialized } : {})}
            aria-busy={asyncEnabled ? (loading || asyncStatus === "loading") : undefined}
            className={classes}
            id={id}
            ref={outerDivRef}
            style={{position: "relative"}}
        >
            <DropdownContext.Provider
                value={{
                    asyncEnabled,
                    asyncStatus: asyncStatusText,
                    creationEnabled,
                    creationOption,
                    renderOption,
                    optionKey,
                    isControlled,
                    onInputChange,
                    isSameOption,
                    selectedOptionIds,
                    getOptionValue,
                    activeStyle,
                    autocomplete,
                    blankSelection,
                    clearable,
                    dropdownContainerRef,
                    disabled,
                    error,
                    errorId,
                    enablePillReorder: isPillReorderEnabled,
                    externalDragHandle,
                    filterItem,
                    filteredOptions,
                    focusedOptionIndex,
                    label,
                    formPillProps,
                    handleBackspace,
                    handleChange,
                    handleOptionClick,
                    handleSelectionChange,
                    handleWrapperClick,
                    inputRef,
                    inputWrapperRef,
                    isDropDownClosed,
                    isInputFocused,
                    floatingOwnerId,
                    floatingShellClasses,
                    portalHost,
                    selectId,
                    multiSelect,
                    noOptionsText,
                    onSelect,
                    optionsWithBlankSelection,
                    selected,
                    setFilterItem,
                    setFocusedOptionIndex,
                    setIsDropDownClosed,
                    setIsInputFocused,
                    setSelected,
                    showPillIndex,
                    toggleDropdown
                }}
            >
                {enableFormSupport && (
                    <DropdownFormFields
                        disabled={disabled}
                        multiSelect={multiSelect}
                        name={name}
                        onInvalid={handleFormInvalid}
                        onReset={handleFormReset}
                        required={required}
                        selected={selectedArray}
                        validationMessage={validationMessage}
                    />
                )}
                {label && (
                  <label
                      data-dropdown="pb-dropdown-label"
                      htmlFor={selectId}
                      onClick={handleLabelClick}
                  >
                    {requiredIndicator ? (
                      <Caption
                          className="pb_dropdown_kit_label"
                          color="lighter"
                          dark={dark}
                          marginBottom="xs"
                      >
                        {label} <span style={{ color: `${colors.error}` }}>*</span>
                      </Caption>
                    ) : (
                      <Caption
                          className="pb_dropdown_kit_label"
                          color="lighter"
                          dark={dark}
                          marginBottom="xs"
                          text={label}
                      />
                    )}
                  </label>
                )}
                <div className={`dropdown_wrapper ${error ? 'error' : ''}`}
                    onBlur={() => {
                        // Debounce to delay the execution to prevent jumpiness in Focus state
                        setTimeout(() => {
                            const active = document.activeElement;
                            if (!active) {
                                setIsInputFocused(false);
                                return;
                            }
                            const inTrigger = dropdownRef.current?.contains(active);
                            const inMenu =
                                dropdownContainerRef.current?.contains(active);
                            if (!inTrigger && !inMenu) {
                                setIsInputFocused(false);
                            }
                        }, 0);
                    }}
                    onFocus={() => !disabled && setIsInputFocused(true)}
                    ref={dropdownRef}
                >
                    {children ? (
                        <>
                            {componentsToRender.map((component, index) => (
                                <React.Fragment key={index}>{component}</React.Fragment>
                            ))}
                        </>
                    ) : (
                        <>
                            <DropdownTrigger placeholder={placeholder} />
                            <DropdownContainer constrainHeight={constrainHeight}>
                                {optionsWithBlankSelection &&
                                    optionsWithBlankSelection?.map((option: GenericObject) => (
                                        <DropdownOption key={optionKey(option)}
                                            option={option}
                                        />
                                    ))}
                            </DropdownContainer>
                        </>
                    )}

                    {error && (
                        <Body
                            aria={{ atomic: "true", live: "polite" }}
                            dark={dark}
                            htmlOptions={{ role: "alert" }}
                            id={errorId}
                            status="negative"
                            text={error}
                        />
                    )}
                </div>
            </DropdownContext.Provider>
        </div>
    )
}

Dropdown = forwardRef(Dropdown) as unknown as DropdownComponent;
(Dropdown as DropdownComponent).displayName = "Dropdown";
(Dropdown as DropdownComponent).Option = DropdownOption;
(Dropdown as DropdownComponent).Trigger = DropdownTrigger;
(Dropdown as DropdownComponent).Container = DropdownContainer;

export default Dropdown;
